import { Dialog } from "construct-ui";
import paginationComponent from "../../../../components/pagination/pagination.component";
import HHPDistyCoverageModel, { hhpDistyCoverageDownloadParamsMapping } from "../../../../models/reports/psi/hhp-daily-coverage.model";
import ViewBase from "../../../view-base.view";

/**
 * A dialog that display details of a selected Disty Coverage
 * 
 * @author cmcnkhiep
 */

export default function HHPDistyCoverageDetailDialog() {
  ViewBase.call(this);

  const DEFAULT_PAGINATION = {
    pageIndex: 1,
    pageSize: 10,
    totalCount: 0
  }

  let searchDetailPayload;
  let detailPagination = structuredClone(DEFAULT_PAGINATION);
  let detailTableData = [];
  let dynamicColumns = [];

  const requestParams = {
    get pageIndex() {
      return detailPagination.pageIndex;
    },

    get pageSize() {
      return detailPagination.pageSize;
    },

    get dateType() {
      return searchDetailPayload.dateType;
    },

    get fromDate() {
      return searchDetailPayload.fromDate;
    },

    get toDate() {
      return searchDetailPayload.toDate;
    },

    get coverageType() {
      return searchDetailPayload.coverageType;
    },

    get modelAliases() {
      return searchDetailPayload.modelAliases;
    },

    get isExcelDownload() {
      return searchDetailPayload.isExcelDownload;
    },

    get distyCode() {
      return searchDetailPayload.distyCode;
    }

  }

  const getDetail = () => {
    this.showLoading();
    HHPDistyCoverageModel.getHHPDistyCoverageDetail(requestParams)
      .then((result) => {
        if (result.data.length === 0) {
          this.showErrorMessage(this.getText("EM00000141", "No records found."));
          return
        }
        detailPagination.totalCount = result.total;
        detailTableData = result.data;
        let { ID, CHANNEL_CD, CHANNEL_NM, GRADE, ...restAttrs } = detailTableData[0]
        dynamicColumns = Object.keys(restAttrs).sort()
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  }

  let downloadDetails = (e, type = '', excelSheetNames = [''], fileName = '') => {
    e.preventDefault();

    searchDetailPayload.isExcelDownload = true;
    HHPDistyCoverageModel.downloadHHPDistyCoverage(hhpDistyCoverageDownloadParamsMapping[type].name, structuredClone(requestParams), excelSheetNames, fileName)
      .catch((error) => {
        if (error.code === apiStatusCodes.noContent) {
          this.showErrorMessage(this.getText("LT00000317", "No results found"))
        } else {
          this.handleError(error);
        }
      })
      .finally(() => {
        searchDetailPayload.isExcelDownload = false;
      })

    return false;
  }

  let onPageSizeChanged = (pageIndex, pageSize) => {
    detailPagination.pageIndex = pageIndex;
    detailPagination.pageSize = pageSize;
    getDetail();
  }

  let onPageChanged = (pageIndex) => {
    detailPagination.pageIndex = pageIndex;
    getDetail();
  }

  return {
    oninit: (vnode) => {
      searchDetailPayload = vnode.attrs.filters;
      getDetail();
    },
    view: ({ attrs: { isOpen, onClose, filters } }) => {
      return m(Dialog,
        {
          isOpen,
          onClose,
          class: 'cui-dialog-xl',
          title: this.getText('LT00001176', 'Disty Coverage Detail'),
          closeOnOutsideClick: false,
          content: [
            m('.card.mb-3',
              m('.card-body',
                m('.row',
                  m('.col-2', this.getText('LT00000778', 'Code') + ':'),
                  m('.col-3', filters.distyCode),
                  m('.col-2', this.getText('LT00000011', 'Name') + ':'),
                  m('.col-5', filters.distyName)
                ),
                m('.row.pt-3',
                  m('.col-2', this.getText('LT00000150', 'Period') + ':'),
                  m('.col-3', `${filters.dateType === 'W' ? 'WEEK' : 'MONTH'} ${filters.fromDate} ~ ${filters.toDate}`),
                  m('.col-2', this.getText('LT00000717', 'Model') + ':'),
                  m('.col-5', filters.modelAliases.split('^').map(alias => alias.trim()).join(', '))
                )
              )
            ),
            m('.card',
              m('.card-body',
                m('.row.gap-3',
                  m('.col-12.d-flex.justify-content-end',
                    m('button.btn.btn-primary',
                      {
                        disabled: detailTableData.length === 0,
                        onclick: (e) => { downloadDetails(e, 'SellthruCoverageDetail', ["Sellthru Coverage Detail"], "SellthruCoverageDetail") }
                      },
                      searchDetailPayload.isExcelDownload
                        ? m('span.spinner-border.spinner-border-sm.me-2[role=status][aria-hidden=true]')
                        : m('i.bi.bi-file-earmark-arrow-down-fill'),
                      this.getText('LT00001002', 'Download Result')
                    )
                  ),
                  m('.col-12',
                    m('.table-responsive',
                      m('table.table.table-hover',
                        m('thead',
                          m('tr.align-middle',
                            m('th[scope="col"]', this.getText('LT00000723', 'Site Code')),
                            m('th[scope="col"]', this.getText('LT00000144', 'Site Name')),
                            m('th[scope="col"]', this.getText('LT00001177', 'Grade')),
                            dynamicColumns.map(col => m('th[scope="col"]', col))
                          )
                        ),
                        m('tbody',
                          (detailTableData.length == 0) &&
                          m('tr.align-middle',
                            m('td[colSpan=13]', this.getText("EM00000141", "No records found."))
                          ),
                          detailTableData.length > 0 &&
                          detailTableData.map((row, index) =>
                            m('tr.align-middle',
                              m('td', m('span.cursor-text', row.CHANNEL_CD)),
                              m('td', m('span.cursor-text', row.CHANNEL_NM)),
                              m('td', m('span.cursor-text', row.GRADE)),
                              dynamicColumns.map(col => m('td', { style: Number(row[col]) <= 0 ? 'background: var(--red-danger)' : '' }, row[col]))
                            )
                          )
                        )
                      )
                    ),
                    detailTableData.length > 0 &&
                    m(".px-3",
                      m(paginationComponent, {
                        currentPage: detailPagination.pageIndex,
                        pageSize: detailPagination.pageSize,
                        totalItems: detailPagination.totalCount,
                        onPageChanged: onPageChanged,
                        onPageSizeChanged: onPageSizeChanged
                      })
                    )
                  ),
                )
              )
            ),
            this.getBaseViews()
          ],
          footer: [
            m('.d-flex.justify-content-end',
              m('button.btn.btn-white', {
                onclick: onClose,
              }, this.getText('LT00000021', 'Close')),
            )
          ]
        }
      );
    }
  }
}
